import {
    DTOCardFormField,
} from 'app/types/form.types'
import removeUnauthorizedField from 'app/utils/removeUnauthorizedField'
import {
    ContractCurrency, LeaseType,
    QuoteStatus,
} from 'app/enums/label.enums'
import {
    Contact,
} from 'app/types/common.types'
import {
    TypeIcon,
} from 'app/shared-components/NotificationIcon/notificationIcon.types'
import {
    SizeLink, TypeLink,
} from 'app/shared-components/SkyNetLink'

import {
    AQuote,
} from '../AQuotes.types'

type QuoteInformationFields = AQuote & {
    accountContacts: Contact[],
    businessDevelopers: Contact[],
    keyAccountManager: Contact,
    contractBasisNumber?: string,
}

const getFields = ({
    getAllowedValues,
    exists,
    disabled,
    enabledContractBasis,
    quoteStatus,
    showContractBasisLink,
    contractBasisNumber,
    hasPlannedContractBasis,
    contractBasisHasValidAdditionalFees,
    contractBasisReadPermission,
    linkedContractBasis,
}: {
    getAllowedValues: (enumValue: Record<string, string>) => Record<string, string>,
    exists: boolean,
    disabled: boolean,
    enabledContractBasis: boolean,
    quoteStatus: QuoteStatus,
    showContractBasisLink: boolean,
    contractBasisNumber: string,
    hasPlannedContractBasis: boolean,
    contractBasisHasValidAdditionalFees: boolean,
    contractBasisReadPermission: boolean,
    linkedContractBasis: string,
}): DTOCardFormField<keyof QuoteInformationFields>[] => {
    const isCancelledOrClosed = [
        QuoteStatus.CANCELLED,
        QuoteStatus.CLOSED,
    ].includes(quoteStatus)

    return [{
        id: 'quoteInformation',
        title: 'Quote Information',
        fields: [
            {
                id: 'column1',
                fields: [
                    {
                        name: 'account',
                        componentName: 'AccountSelector',
                        labelKey: 'AccountQuotes.accountName',
                        required: true,
                        disabled: disabled || exists,
                    },
                    {
                        name: 'quoteStatus',
                        labelKey: 'AccountQuotes.quoteStatus',
                        allowedValues: getAllowedValues(QuoteStatus),
                        componentName: 'EnumSelector',
                        disabled: true,
                    },
                    ...removeUnauthorizedField([{
                        name: 'plannedContractBasis',
                        className: 'contractBasis',
                        componentName: 'ContractBasisSelector',
                        disabled: !enabledContractBasis || disabled,
                        required: true,
                        labelKey: 'AccountQuotes.plannedContractBasis',
                    }], ['plannedContractBasis'], contractBasisReadPermission),
                    {
                        name: 'currency',
                        componentName: 'EnumSelector',
                        allowedValues: getAllowedValues(ContractCurrency),
                        labelKey: 'AccountQuotes.currency',
                        required: true,
                        useDropdown: true,
                        disabled: disabled || exists,
                    },
                    {
                        name: 'leaseType',
                        componentName: 'EnumSelector',
                        allowedValues: getAllowedValues(LeaseType),
                        labelKey: 'AccountQuotes.leaseType',
                        required: true,
                        useDropdown: true,
                        disabled: disabled || exists,
                    },
                    ...removeUnauthorizedField([{
                        name: 'contractBasisNumber',
                        className: 'contractBasisNumber',
                        labelKey: 'ContractBasis.assignedContractBasisId',
                        notificationIconProps: contractBasisHasValidAdditionalFees ? undefined : {
                            content: 'Please add valid Additional Fees to this Contracts Basis \n'
                                + 'before generating a quote document.',
                            type: TypeIcon.WARNING,
                        },
                        type: TypeLink.EXTERNAL,
                        size: SizeLink.S,
                        componentName: 'SkyNetLink',
                        text: contractBasisNumber,
                        link: linkedContractBasis,
                    }], ['contractBasisNumber'], (showContractBasisLink && contractBasisReadPermission && linkedContractBasis)),
                ],
            },
            {
                id: 'column2',
                fields: [
                    {
                        name: 'quotedForCompany',
                        componentName: 'LocationObjectSelector',
                        includeFilters: {
                            isProspect: [
                                true,
                                false,
                            ],
                        },
                        labelKey: 'AccountQuotes.quotedForCompany',
                        required: true,
                        disabled: disabled || exists,
                    },
                    {
                        name: 'quotedFor',
                        componentName: 'ContactObjectSelector',
                        notificationIconProps: {
                            content: 'This contact will be shown in the quote document as a Customer Contact.',
                            type: TypeIcon.INFO,
                        },
                        labelKey: 'AccountQuotes.quotedFor',
                        required: true,
                        disabled: disabled || (!enabledContractBasis && exists),
                        showOptionLabelWithEmail: true,
                    },
                ],
            },
            {
                id: 'column3',
                fields: [
                    {
                        name: 'isPerformanceQualification',
                        componentName: 'Checkbox',
                        labelKey: 'AccountQuotes.isPerformanceQualification',
                        disabled: disabled || exists,
                    },
                    {
                        name: 'comment',
                        componentName: 'LongText',
                        labelKey: 'AccountQuotes.comment',
                        disabled,
                    },
                ],
            },
            {
                id: 'column4',
                fields: [
                    {
                        name: 'pricingResponsible',
                        componentName: 'MultipleUserSelector',
                        labelKey: 'AccountQuotes.pricingResponsible',
                        required: QuoteStatus.IN_PROGRESS === quoteStatus
                            || (QuoteStatus.CREATED === quoteStatus && hasPlannedContractBasis),
                        disabled: isCancelledOrClosed || disabled,
                    },
                    {
                        name: 'skyCellContact',
                        componentName: 'UserObjectSelector',
                        required: true,
                        disabled: isCancelledOrClosed || disabled,
                        labelKey: 'AccountQuotes.skycellContact',
                    },
                    {
                        name: 'createdOn',
                        componentName: 'DateTimeSelect',
                        disabled: true,
                        labelKey: 'Accounts.createdOn',
                    },
                    {
                        name: 'createdBy',
                        componentName: 'InputSingleline',
                        labelKey: 'KamDashboard.requestor',
                        disabled: true,
                    },
                ],
            },
        ],
    }]
}

export default getFields
